import type { PortfolioProjectFilterValues } from "@/components/ProjectFilter/PortfolioProjectFilterFields";
import { parseQueryParamToList } from "@/helpers/query-params";

export const PORTFOLIO_PROJECT_FILTER_QUERY_PARAM_KEYS = ["owner", "maintenance", "city", "projectIds"] as const;

export type PortfolioProjectFilterQueryParamKey = (typeof PORTFOLIO_PROJECT_FILTER_QUERY_PARAM_KEYS)[number];
export type PortfolioProjectFilterQueryParams = Partial<Record<PortfolioProjectFilterQueryParamKey, string | string[]>>;
type SerializedPortfolioProjectFilterQueryParams = Partial<Record<PortfolioProjectFilterQueryParamKey, string>>;

function parseQueryParamValue(value: string | string[] | undefined): string[] | undefined {
  if (Array.isArray(value)) {
    const values = value.flatMap((item) => parseQueryParamToList(item) ?? []);

    return values.length ? values : undefined;
  }

  return parseQueryParamToList(value);
}

export function getPortfolioProjectFiltersFromQueryParams(
  queryParams: PortfolioProjectFilterQueryParams,
  supportedKeys: readonly PortfolioProjectFilterQueryParamKey[] = PORTFOLIO_PROJECT_FILTER_QUERY_PARAM_KEYS,
): PortfolioProjectFilterValues {
  return Object.fromEntries(supportedKeys.map((key) => [key, parseQueryParamValue(queryParams[key])]));
}

export function getPortfolioProjectFilterQueryParams(
  filters: PortfolioProjectFilterValues,
  supportedKeys: readonly PortfolioProjectFilterQueryParamKey[] = PORTFOLIO_PROJECT_FILTER_QUERY_PARAM_KEYS,
): SerializedPortfolioProjectFilterQueryParams {
  return Object.fromEntries(
    supportedKeys.map((key) => [key, filters[key]?.length ? filters[key]?.join(",") : undefined]),
  );
}

export function addPortfolioProjectFilterQueryParams(path: string, sourceSearch: string): string {
  const [pathWithoutHash, hash = ""] = path.split("#", 2);
  const [pathname, targetSearch = ""] = pathWithoutHash.split("?", 2);
  const sourceParams = new URLSearchParams(sourceSearch);
  const targetParams = new URLSearchParams(targetSearch);

  for (const key of PORTFOLIO_PROJECT_FILTER_QUERY_PARAM_KEYS) {
    const values = sourceParams.getAll(key).filter(Boolean);
    if (values.length) {
      targetParams.set(key, values.join(","));
    }
  }

  const queryString = targetParams.toString();
  const nextPath = queryString ? `${pathname}?${queryString}` : pathname;

  return hash ? `${nextPath}#${hash}` : nextPath;
}
